import classnames from 'classnames'
import { observer } from 'mobx-react-lite'
import {
  cloneElement,
  isValidElement,
  useCallback,
  useEffect,
  useReducer,
  type CSSProperties,
  type FC,
  type ReactElement,
  type ReactNode,
  type RefObject,
} from 'react'
import { useWindowSize } from 'usehooks-ts'
import { DraggableCore, type DraggableData } from 'react-draggable'
import { getBoundPosition } from 'react-draggable/build/cjs/utils/positionFns'

type IDraggableWrapperProps = {
  children: (ref: RefObject<HTMLDivElement>) => ReactNode
  defaultPosition?: {
    x: number
    y: number
  }
  onChangeRef?: (element: HTMLDivElement) => void
  draggleRef: RefObject<HTMLDivElement>
}

type IDragState = {
  x: number
  y: number
  slackX: number
  slackY: number
  dragging: boolean
  dragged: boolean
}

type ITransformableChildProps = {
  className?: string
  style?: CSSProperties
}

type IDragAction =
  | { type: 'start' }
  | { type: 'stop' }
  | { type: 'drag'; coreData: DraggableData; getNode: () => HTMLDivElement | null }
  | { type: 'rebound'; x: number; y: number }

const SCALE = 1
const DEFAULT_POSITION = { x: 0, y: 0 }

const uiDragData = (state: { x: number; y: number }, coreData: DraggableData): DraggableData => ({
  node: coreData.node,
  x: state.x + coreData.deltaX / SCALE,
  y: state.y + coreData.deltaY / SCALE,
  deltaX: coreData.deltaX / SCALE,
  deltaY: coreData.deltaY / SCALE,
  lastX: state.x,
  lastY: state.y,
})

const dragReducer = (state: IDragState, action: IDragAction): IDragState => {
  switch (action.type) {
    case 'start':
      return { ...state, dragging: true, dragged: true }
    case 'stop':
      return { ...state, dragging: false, slackX: 0, slackY: 0 }
    case 'rebound':
      return {
        ...state,
        x: action.x,
        y: action.y,
        slackX: 0,
        slackY: 0,
      }
    case 'drag': {
      const uiData = uiDragData({ x: state.x, y: state.y }, action.coreData)
      const beforeSlackX = uiData.x
      const beforeSlackY = uiData.y
      const withSlackX = uiData.x + state.slackX
      const withSlackY = uiData.y + state.slackY

      const boundsProxy = {
        props: { bounds: 'body' as const },
        findDOMNode: () => {
          const node = action.getNode()
          if (!node) {
            throw new Error('<DraggableWrapper>: node ref missing during drag')
          }
          return node
        },
      }

      const [bx, by] = getBoundPosition(boundsProxy, withSlackX, withSlackY)

      return {
        ...state,
        x: bx,
        y: by,
        slackX: state.slackX + (beforeSlackX - bx),
        slackY: state.slackY + (beforeSlackY - by),
      }
    }
    default:
      return state
  }
}

const initDragState = (defaultPosition: { x: number; y: number }): IDragState => ({
  x: defaultPosition.x,
  y: defaultPosition.y,
  slackX: 0,
  slackY: 0,
  dragging: false,
  dragged: false,
})

const isTransformableChild = (node: ReactNode): node is ReactElement<ITransformableChildProps> => {
  return isValidElement(node)
}

export const DraggableWrapper: FC<IDraggableWrapperProps> = observer(
  ({ draggleRef, onChangeRef, children, defaultPosition = DEFAULT_POSITION }) => {
    const [dragState, dispatch] = useReducer(dragReducer, defaultPosition, initDragState)
    const { width: windowWidth = 0, height: windowHeight = 0 } = useWindowSize({
      debounceDelay: 100,
    })

    useEffect(() => {
      if (!draggleRef.current) return
      if (!onChangeRef) return

      const mutationObserver = new MutationObserver((mutationList) => {
        if (!draggleRef.current) return

        for (const mutation of mutationList) {
          if (mutation.type === 'childList') {
            onChangeRef(draggleRef.current)
          }
        }
      })

      mutationObserver.observe(draggleRef.current, {
        attributes: true,
        childList: true,
        subtree: true,
      })

      return () => {
        if (!onChangeRef) return

        mutationObserver.disconnect()
      }
    }, [])

    const onStart = useCallback(() => {
      dispatch({ type: 'start' })
    }, [])

    const onDrag = useCallback(
      (_: unknown, coreData: DraggableData) => {
        dispatch({ type: 'drag', coreData, getNode: () => draggleRef.current })
      },
      [draggleRef]
    )

    const onStop = useCallback(() => {
      dispatch({ type: 'stop' })
    }, [])

    useEffect(() => {
      if (!draggleRef.current) return
      if (dragState.dragging) return

      const boundsProxy = {
        props: { bounds: 'body' as const },
        findDOMNode: () => {
          const node = draggleRef.current
          if (!node) {
            throw new Error('<DraggableWrapper>: node ref missing during resize')
          }
          return node
        },
      }

      const [bx, by] = getBoundPosition(boundsProxy, dragState.x, dragState.y)
      const isPositionChanged = bx !== dragState.x || by !== dragState.y

      if (!isPositionChanged) return

      dispatch({ type: 'rebound', x: bx, y: by })
    }, [windowWidth, windowHeight, dragState.x, dragState.y, dragState.dragging, draggleRef])

    const child = children(draggleRef)

    if (!isTransformableChild(child)) return null

    return (
      <DraggableCore
        nodeRef={draggleRef}
        handle={'strong.drag-handle'}
        onStart={onStart}
        onDrag={onDrag}
        onStop={onStop}
      >
        {cloneElement(child, {
          className: classnames(child.props.className, 'react-draggable', {
            'react-draggable-dragging': dragState.dragging,
            'react-draggable-dragged': dragState.dragged,
          }),
          style: {
            ...child.props.style,
            transform: `translate(${dragState.x}px, ${dragState.y}px)`,
          },
        })}
      </DraggableCore>
    )
  }
)
